'use client';

import { useCallback, useRef } from 'react';
import type { KeyboardEvent, MouseEvent } from 'react';

/**
 * How long after a key-triggered activation a keyboard-originated click is
 * treated as the same press. A browser that still synthesises the button's
 * click after the cancelled key press delivers it within a few milliseconds;
 * a screen reader's own activation arrives with no key press before it.
 */
export const KEYBOARD_CLICK_GRACE_MS = 300;

const isActivationKey = (key: string) => key === 'Enter' || key === ' ';

/**
 * Explicit keyboard activation for a disclosure button.
 *
 * A native `<button>` should open on Enter and Space by itself, but on
 * testing1 the mega-menu triggers did not (WB-813): focus landed on the
 * trigger, `aria-expanded` never changed, only a pointer got through. Rather
 * than depend on the browser turning the key press into a click, the press is
 * handled here directly and its default is cancelled. `onClick` then skips a
 * click that is only the echo of that press (`detail === 0`, no pointer), so
 * no browser can toggle twice — while a click without a key press first, the
 * way assistive technology activates a control, still goes through.
 */
export const useKeyboardActivation = (onActivate: () => void) => {
  const lastKeyboardActivation = useRef(0);

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      if (!isActivationKey(event.key)) return;

      // Also keeps Space from scrolling the page.
      event.preventDefault();
      lastKeyboardActivation.current = performance.now();
      onActivate();
    },
    [onActivate],
  );

  const onClick = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      const isKeyboardEcho =
        event.detail === 0 &&
        performance.now() - lastKeyboardActivation.current <
          KEYBOARD_CLICK_GRACE_MS;

      if (isKeyboardEcho) return;

      onActivate();
    },
    [onActivate],
  );

  return { onKeyDown, onClick };
};

export default useKeyboardActivation;
